import * as Cesium from 'cesium';

type Point = [number, number];
type Profile = {
  points: Cesium.Cartographic[];
  groundHeights: number[];
  deckHeights: number[];
  width: number;
};

// Cartographicはラジアン。1度当たりの距離をそのまま使うと幅が約57倍になる。
const METERS_PER_LATITUDE = 110900 * 180 / Math.PI;

export function edgeRing(points: Cesium.Cartographic[], heights: number[], width: number, side: 1 | -1): Cesium.Cartesian3[] {
  return points.map((point, index) => {
    const previous = points[Math.max(0, index - 1)];
    const next = points[Math.min(points.length - 1, index + 1)];
    const metersPerLongitude = 111320 * 180 / Math.PI * Math.cos(point.latitude);
    const dx = (next.longitude - previous.longitude) * metersPerLongitude;
    const dy = (next.latitude - previous.latitude) * METERS_PER_LATITUDE;
    const length = Math.hypot(dx, dy) || 1;
    const east = -dy / length * width / 2 * side;
    const north = dx / length * width / 2 * side;
    return Cesium.Cartesian3.fromRadians(
      point.longitude + east / metersPerLongitude,
      point.latitude + north / METERS_PER_LATITUDE,
      heights[index],
    );
  });
}

function makeDeckRing(points: Cesium.Cartographic[], heights: number[], width: number): Cesium.Cartesian3[] {
  return [...edgeRing(points, heights, width, 1), ...edgeRing(points, heights, width, -1).reverse()];
}

function nearestDeckHeight(profile: Profile, position: Cesium.Cartographic): number {
  let bestIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < profile.points.length; i += 1) {
    const dx = (profile.points[i].longitude - position.longitude) * 111320 * Math.cos(position.latitude);
    const dy = (profile.points[i].latitude - position.latitude) * METERS_PER_LATITUDE;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = i;
    }
  }
  return profile.deckHeights[bestIndex];
}

/** 橋梁区間を端部の道路高に合わせた勾配付きの概略モデルへ変換する。 */
export async function elevateBridges(viewer: Cesium.Viewer, data: Cesium.GeoJsonDataSource): Promise<void> {
  const time = viewer.clock.currentTime;
  const bridges = data.entities.values.filter(entity => entity.properties?.getValue(time).kind === 'bridge');
  const plans = bridges.map(entity => {
    const properties = entity.properties!.getValue(time);
    const centerline = properties.centerline as Point[];
    if (!centerline?.length) return undefined;
    const samples: Cesium.Cartographic[] = [];
    for (let i = 1; i < centerline.length; i += 1) {
      const start = Cesium.Cartographic.fromDegrees(...centerline[i - 1]);
      const end = Cesium.Cartographic.fromDegrees(...centerline[i]);
      const geodesic = new Cesium.EllipsoidGeodesic(start, end);
      const count = Math.max(1, Math.ceil(geodesic.surfaceDistance / 35));
      for (let j = 0; j < count; j += 1) samples.push(geodesic.interpolateUsingFraction(j / count));
    }
    samples.push(Cesium.Cartographic.fromDegrees(...centerline[centerline.length - 1]));
    return { entity, properties, samples };
  }).filter((plan): plan is NonNullable<typeof plan> => plan !== undefined);
  if (!plans.length) return;

  const allSamples = plans.flatMap(plan => plan.samples);
  let sampledTerrain = allSamples;
  try {
    sampledTerrain = await Promise.race([
      Cesium.sampleTerrain(viewer.terrainProvider, 12, allSamples),
      new Promise<never>((_, reject) => window.setTimeout(() => reject(new Error('橋梁標高の取得タイムアウト')), 15000)),
    ]);
  } catch (error) {
    console.warn('[Bridge] 標高取得失敗区間は既存地形を使用', error);
  }
  if (viewer.isDestroyed()) return;

  const profiles = new Map<number, Profile>();
  let sampleOffset = 0;
  data.entities.suspendEvents();
  try {
    for (const { entity, properties, samples: points } of plans) {
      const terrainPoints = sampledTerrain.slice(sampleOffset, sampleOffset + points.length);
      sampleOffset += points.length;
      const groundHeights = points.map((point, index) => {
        const sampled = terrainPoints[index]?.height;
        return Number.isFinite(sampled) ? sampled : viewer.scene.globe.getHeight(point) ?? 0;
      });
      // 橋端は周辺道路の標高に合わせ、中央だけを緩やかに持ち上げる。
      const first = groundHeights[0] ?? 0;
      const last = groundHeights.at(-1) ?? first;
      const bridgeRise = Math.min(0.6, Math.max(0.15, properties.bridgeLayer > 1 ? 0.35 : 0.2));
      const deckHeights = groundHeights.map((_, index) => {
        const ratio = points.length <= 1 ? 0 : index / (points.length - 1);
        const roadGrade = first + (last - first) * ratio;
        return roadGrade + Math.sin(Math.PI * ratio) * bridgeRise;
      });
      const profile = { points, groundHeights, deckHeights, width: Number(properties.width) || 6 };
      profiles.set(Number(properties.osm_id), profile);

      const polygon = entity.polygon!;
      polygon.heightReference = new Cesium.ConstantProperty(Cesium.HeightReference.NONE);
      polygon.perPositionHeight = new Cesium.ConstantProperty(true);
      polygon.height = undefined;
      polygon.extrudedHeight = undefined;
      polygon.hierarchy = new Cesium.ConstantProperty(new Cesium.PolygonHierarchy(makeDeckRing(points, deckHeights, profile.width)));
      polygon.material = new Cesium.ColorMaterialProperty(Cesium.Color.fromCssColorString('#51565b'));
      entity.name = `${properties.bridgeName}（端部接続・高さは推定）`;

      const left = edgeRing(points, deckHeights, profile.width, 1).map(position => Cesium.Cartographic.fromCartesian(position));
      const right = edgeRing(points, deckHeights, profile.width, -1).map(position => Cesium.Cartographic.fromCartesian(position));
      for (const [side, edge] of [left, right].entries()) {
        data.entities.add({
          id: `bridge-girder-${properties.osm_id}-${side}`,
          name: `${properties.bridgeName} / 橋桁（推定）`,
          wall: {
            positions: edge.map(point => Cesium.Cartesian3.fromRadians(point.longitude, point.latitude)),
            minimumHeights: edge.map((_, index) => deckHeights[index] - 1),
            maximumHeights: deckHeights,
            material: Cesium.Color.fromCssColorString('#4b5055'),
          },
        });
        data.entities.add({
          id: `bridge-rail-${properties.osm_id}-${side}`,
          name: `${properties.bridgeName} / 高欄（推定）`,
          wall: {
            positions: edge.map(point => Cesium.Cartesian3.fromRadians(point.longitude, point.latitude)),
            minimumHeights: deckHeights.map(height => height + 0.52),
            maximumHeights: deckHeights.map(height => height + 1.0),
            material: Cesium.Color.fromCssColorString('#a8adb0'),
          },
        });
      }

      let lastPierDistance = -Infinity;
      let distance = 0;
      for (let index = 0; index < points.length; index += 1) {
        if (index) distance += new Cesium.EllipsoidGeodesic(points[index - 1], points[index]).surfaceDistance;
        if (index !== 0 && index !== points.length - 1 && distance - lastPierDistance < 30) continue;
        lastPierDistance = distance;
        const pierHeight = deckHeights[index] - 1 - groundHeights[index];
        if (pierHeight <= 0.5) continue;
        data.entities.add({
          id: `bridge-pier-${properties.osm_id}-${index}`,
          name: `${properties.bridgeName} / 橋脚（推定）`,
          position: Cesium.Cartesian3.fromRadians(points[index].longitude, points[index].latitude, groundHeights[index] + pierHeight / 2),
          cylinder: {
            length: pierHeight,
            topRadius: Math.min(1.3, profile.width / 5),
            bottomRadius: Math.min(1.5, profile.width / 4),
            slices: 12,
            material: Cesium.Color.fromCssColorString('#939594'),
          },
        });
      }
    }

    for (const entity of data.entities.values) {
      if (!entity.polyline) continue;
      const osmId = Number(entity.properties?.getValue(time).osm_id);
      const profile = profiles.get(osmId);
      if (!profile) continue;
      const positions = entity.polyline.positions!.getValue(time) as Cesium.Cartesian3[];
      entity.polyline.clampToGround = new Cesium.ConstantProperty(false);
      // 高架上へ持ち上げた線は地表用zIndexの対象外。残すとCesiumが警告し、zIndexも無視される。
      entity.polyline.zIndex = undefined;
      entity.polyline.positions = new Cesium.ConstantProperty(positions.map(position => {
        const cartographic = Cesium.Cartographic.fromCartesian(position);
        return Cesium.Cartesian3.fromRadians(cartographic.longitude, cartographic.latitude, nearestDeckHeight(profile, cartographic) + 0.06);
      }));
    }
  } finally {
    data.entities.resumeEvents();
    viewer.scene.requestRender();
  }
}
